import React, {useReducer, useEffect} from 'react'
import ListSelect from '../../../../ListSelect/ListSelect'
import Spinner from '../../../../Spinner/Spinner'
import {useViewState} from '../../../../../hooks/form-state'
import FormInput from '../../../../Form/Input'
import FormField from '../../../../Form/FormField'
import {useUIState} from '../../../../../context/UIContext'
import {useUserState} from '../../../../../context/UserContext'
import {fetchDepartmentsByFloor} from '../../../../../api/departments'
import {saveVisitor} from '../../../../../api/visitor'
import {_L, useLocaleState} from '../../../../../context/LocaleContext'
import styled from 'styled-components'

const USER_NAMED = 'USER_NAMED'
const COMPANY_INFO = 'COMPANY_INFO'
const USER_INFO = 'USER_INFO'

const initialState = {
  firstName: '',
  lastName: '',
  company: '',
  phone: '',
  email: '',
  department: '',
  dv_id: '',
  dp_id: '',
  loading: false,
  errors: {firstName: null, lastName: null},
  departmentsByFloor: []
}

const reducer = (state, action) => {
  switch (action.type) {
    case 'setData':
      return {
        ...state,
        ...action.payload
      }

    case 'setDepartments':
      return {
        ...state,
        departmentsByFloor: action.payload
      }

    case 'setErrors':
      return {
        ...state,
        errors: {
          ...state.errors,
          ...action.payload
        }
      }
    default:
      throw new Error('Unknown action type.')
  }
}

const NewVisitor = ({newVisitorCreated}) => {
  const {user} = useUserState()
  const localeState = useLocaleState()
  const {activeFloor} = useUIState()
  const [state, dispatch] = useReducer(reducer, initialState)

  const viewStates = {
    USER_NAMED: {
      prev: () => {},
      next: () => {
        // Check validation
        let errors = validate(state)
        const anyError = Object.keys(errors).some(x => errors[x])
        if (anyError) {
          dispatch({type: 'setErrors', payload: errors})
          return USER_NAMED
        } else {
          return COMPANY_INFO
        }
      }
    },
    COMPANY_INFO: {
      prev: () => {},
      next: () => USER_INFO
    },
    USER_INFO: {
      prev: () => {},
      next: () => {
        return submitVisitor()
      }
    }
  }

  useEffect(
    () => {
      let isMounted = true
      const departments = x => ({
        value: x.deptCode,
        label: x.department
      })
      fetchDepartmentsByFloor(activeFloor).then(departmentsByFloor => {
        if (isMounted) {
          dispatch({
            type: 'setDepartments',
            payload: departmentsByFloor.map(departments)
          })
        }
      })

      return () => {
        isMounted = false
      }
    },
    [activeFloor]
  )

  const {viewState, dispatchViewState} = useViewState({
    viewStates,
    initialView: USER_NAMED
  })

  const onInput = event => {
    const {name, value} = event.target

    dispatch({type: 'setData', payload: {[name]: value}})

    if (name === 'firstName' || name === 'lastName') {
      dispatch({type: 'setErrors', payload: {[name]: null}})
    }
  }

  const onDepartmentChange = value => {
    let [dv_id, dp_id] = value.value.split('|')

    dispatch({
      type: 'setData',
      payload: {
        department: value,
        dv_id,
        dp_id
      }
    })
  }

  const onNext = () => dispatchViewState({action: 'next'})

  const validate = inputs => {
    return {
      firstName:
        inputs.firstName.length > 0
          ? null
          : _L({key: 'First Name is required', localeState}),
      lastName:
        inputs.lastName.length > 0
          ? null
          : _L({key: 'Last Name is required', localeState})
    }
  }

  const submitVisitor = async () => {
    const visitor = await saveVisitor({visitor: state, user})
    newVisitorCreated(visitor)
  }

  const {loading} = state

  const getContent = () => {
    let content
    switch (viewState) {
      case USER_NAMED:
        content = <SelectUserNames state={state} onInput={onInput} />
        break
      case COMPANY_INFO:
        content = (
          <SelectCompanyInfo
            state={state}
            onInput={onInput}
            onDepartmentChange={onDepartmentChange}
            departmentsByFloor={[]}
          />
        )
        break
      case USER_INFO:
        content = <SelectUserInfo state={state} onInput={onInput} />
        break
    }

    return content
  }

  const getButtonContent = () => {
    let button = <div>{_L({key: 'NEXT', localeState})}</div>
    if (viewState === USER_INFO) {
      button = loading ? (
        <Spinner height="30px" width="30px" />
      ) : (
        <div>{_L({key: 'DONE', localeState})}</div>
      )
    }

    return button
  }

  const content = getContent()
  const buttonContent = getButtonContent()

  return (
    <Container>
      <TitleText>{_L({key: 'New Visitor', localeState})}</TitleText>
      {content}
      <ButtonContainer>
        <FormButton onClick={onNext}>{buttonContent}</FormButton>
      </ButtonContainer>
    </Container>
  )
}

const SelectUserNames = ({state, onInput}) => {
  const localeState = useLocaleState()
  const {firstName, lastName, errors} = state
  return (
    <>
      <InputContainer>
        <FormField
          value={firstName}
          name="firstName"
          onChange={onInput}
          label={_L({key: 'First Name', localeState})}
          errors={errors}
        >
          <FormInput />
        </FormField>
      </InputContainer>

      <InputContainer>
        <FormField
          value={lastName}
          name="lastName"
          onChange={onInput}
          label={_L({key: 'Last Name', localeState})}
          errors={errors}
        >
          <FormInput />
        </FormField>
      </InputContainer>
    </>
  )
}

const SelectCompanyInfo = ({state, onDepartmentChange, onInput}) => {
  const localeState = useLocaleState()
  const {firstName, lastName, company, department, departmentsByFloor} = state

  const user = `${firstName} ${lastName}`

  return (
    <div>
      <UserNameText>{user}</UserNameText>
      <InputContainer>
        <FormField
          value={company}
          name="company"
          onChange={onInput}
          label={_L({key: 'Company Name', localeState})}
        >
          <FormInput />
        </FormField>
      </InputContainer>

      <InputContainer>
        <SubTitleText>
          {_L({key: 'Department to visit', localeState})}
        </SubTitleText>
        <ListSelect
          options={departmentsByFloor}
          placeholder=""
          isSearchable={false}
          onChange={onDepartmentChange}
          value={department}
        />
      </InputContainer>
    </div>
  )
}

const SelectUserInfo = ({state, onInput}) => {
  const {firstName, lastName, phone, email, company} = state
  const user = `${firstName} ${lastName}`
  const localeState = useLocaleState()
  return (
    <div>
      <UserInfoContainer>
        <UserNameText>{user}</UserNameText>
        <CompanyNameText>{company}</CompanyNameText>
      </UserInfoContainer>

      <InputContainer>
        <FormField
          value={email}
          name="email"
          onChange={onInput}
          label={_L({key: 'Email', localeState})}
        >
          <FormInput />
        </FormField>
      </InputContainer>

      <InputContainer>
        <FormField
          value={phone}
          name="phone"
          onChange={onInput}
          label={_L({key: 'Phone', localeState})}
        >
          <FormInput />
        </FormField>
      </InputContainer>
    </div>
  )
}

export default NewVisitor

const Container = styled.div`
  width: 600px;
  height: 480px;
  border-radius: 10px;
  background-color: #ffffff;
  box-shadow: 0 10px 40px 0 rgba(0, 0, 0, 0.05);
  box-sizing: border-box;

  @media (max-width: 768px) {
    width: 100vw;
    height: calc(100vh - 80px);
    overflow-y: scroll;
    background-color: white;
    border-radius: 0;
  }
`
const TitleText = styled.div`
  font-size: 30px;
  font-weight: bold;
  color: #000000;
  margin-left: 50px;
  margin-top: 50px;
  margin-bottom: 20px;
  
  @media (max-width: 420px) {
  	font-size: 24px;
    margin-left: 20px;
    margin-bottom: 10px;
  }
`

const InputContainer = styled.div`
  margin-left: 50px;
  margin-right: 50px;
  padding-top: 14px;
  
  @media (max-width: 420px) {
    margin-left: 20px;
  	margin-right: 20px;
  	padding-top: 12px;
  }
`

const SubTitleText = styled.div`
  font-size: 16px;
  letter-spacing: normal;
  padding-top: 10px;
  padding-bottom: 20px;
  color: #a4a9b7;
  
  @media (max-width: 420px) {
    font-size: 14px;
  }
`

const UserNameText = styled(SubTitleText)`
  width: 50%;
  margin-left: 50px;
  text-align: left;
  color: #000;
  
  @media (max-width: 420px) {
    margin-left: 20px;
  }
`

const CompanyNameText = styled(SubTitleText)`
  text-align: right;
  margin-right: 50px;
  color: #3395fe;
  
  @media (max-width: 420px) {
    margin-right: 20px;
  }
`

const ButtonContainer = styled.div`
  display: block;

  @media (max-width: 768px) {
    display: flex;
    justify-content: center;
  }
`

const FormButton = styled.button`
  margin-top: 90px;
  height: 80px;
  border-radius: 0 0 10px 10px;
  font-size: 16px;
  font-weight: 600;
  box-sizing: border-box;
  background-color: #3395fe;
  border: 2px solid #3395fe;
  color: #ffffff;
  outline: none;
  width: 100%;

  @media (max-width: 768px) {
    border-radius: 6px;
    height: 60px;
    margin: 100px 50px 0 50px;
  }
  
  @media (max-width: 420px) {
    height: 45px;
    margin: 30px 20px 40px 20px;
    font-size: 14px;
  }
`

const UserInfoContainer = styled.div`
  display: flex;
  justify-content: space-between;
`
